import {
  displayOffer,
  fullscreenApi,
  isStandalone,
  platformOf,
  showFullscreenReentry,
  type FullscreenApi,
  type Platform,
} from './display';
import './displayShell.css';

const INSTALL_HINT_KEY = 'pancake.installHint.v1';
/** iOS reports the new size a beat after rotating or a toolbar sliding. */
const LATE_REFRESH_MS = 350;
/** Game-pixel inset for the back-to-fullscreen button: snug to the top-left canvas corner. */
const REENTER_GAME_X = 6;
const REENTER_GAME_Y = 6;

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type WebkitDocument = Document & {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type WebkitElement = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
type LockableOrientation = ScreenOrientation & { lock?: (o: string) => Promise<void> };

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function button(className: string, text: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', `pk-shell__btn ${className}`, text);
  b.type = 'button';
  b.addEventListener('click', onClick);
  return b;
}

function readHint(): string | null {
  try {
    return window.localStorage.getItem(INSTALL_HINT_KEY);
  } catch {
    return null;
  }
}

function writeHint(value: string): void {
  try {
    window.localStorage.setItem(INSTALL_HINT_KEY, value);
  } catch {
    // Private mode: the hint just comes back next time.
  }
}

/**
 * The page around the game canvas: the title screen's fullscreen / install
 * corner, the iPhone Home Screen steps, the turn-sideways card, the update
 * notice, and keeping Phaser's FIT in step when the viewport changes.
 */
export class DisplayShell {
  readonly platform: Platform;
  readonly api: FullscreenApi;
  private installed = false;
  private installPrompt: InstallPromptEvent | null = null;
  private titleActive = false;
  private refreshGame: (() => void) | null = null;
  private corner: HTMLDivElement | null = null;
  private sheet: HTMLDivElement | null = null;
  private update: { apply: () => void } | null = null;
  private updateCard: HTMLDivElement | null = null;
  private listeners = new Set<() => void>();
  private inGame = false;
  private requesting = false;
  private reenter: HTMLButtonElement | null = null;
  /** The Phaser location plate is presenting, including either slide tween. */
  private locationPlateVisible = false;

  constructor() {
    const doc = document as WebkitDocument;
    const root = document.documentElement as WebkitElement;
    this.platform = platformOf(navigator.userAgent, navigator.maxTouchPoints ?? 0);
    this.api = fullscreenApi({
      fullscreenEnabled: doc.fullscreenEnabled,
      webkitFullscreenEnabled: doc.webkitFullscreenEnabled,
      hasRequest: typeof root.requestFullscreen === 'function',
      hasWebkitRequest: typeof root.webkitRequestFullscreen === 'function',
    });
  }

  get standalone(): boolean {
    const mq = (q: string) => window.matchMedia?.(q).matches ?? false;
    return isStandalone({
      displayModeStandalone: mq('(display-mode: standalone)') || mq('(display-mode: fullscreen)'),
      navigatorStandalone: (navigator as Navigator & { standalone?: boolean }).standalone,
    });
  }

  get fullscreenSupported(): boolean {
    return this.api !== 'none';
  }

  get isFullscreen(): boolean {
    const doc = document as WebkitDocument;
    return Boolean(doc.fullscreenElement ?? doc.webkitFullscreenElement);
  }

  /** Once, at boot (before the gate): listeners that must be there from the start. */
  install(): void {
    if (this.installed) return;
    this.installed = true;
    const html = document.documentElement;
    html.classList.toggle('pk-standalone', this.standalone);
    html.classList.toggle('pk-ios', this.platform === 'ios');
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.installPrompt = e as InstallPromptEvent;
      this.renderCorner();
    });
    window.addEventListener('appinstalled', () => {
      this.installPrompt = null;
      writeHint('installed');
      this.renderCorner();
    });
    const changed = () => this.viewportChanged();
    window.addEventListener('resize', changed);
    window.addEventListener('orientationchange', changed);
    window.visualViewport?.addEventListener('resize', changed);
    document.addEventListener('fullscreenchange', changed);
    document.addEventListener('webkitfullscreenchange', changed);
    this.mountRotateCard();
  }

  /** Phaser's scale manager, so a rotation or toolbar change re-fits the game. */
  attachGame(refresh: () => void): void {
    this.refreshGame = refresh;
  }

  /** The corner buttons and the update notice show only on the title screen. */
  setTitleActive(on: boolean): void {
    this.titleActive = on;
    this.renderCorner();
    this.renderUpdate();
    if (!on) this.closeSheet();
  }

  /** Play is under way (the overworld is up): the back-to-fullscreen button may show. */
  setInGame(on: boolean): void {
    this.inGame = on;
    this.renderReenter();
  }

  /** The location plate calls this at its actual presentation boundaries. */
  setLocationPlateVisible(visible: boolean): void {
    if (this.locationPlateVisible === visible) return;
    this.locationPlateVisible = visible;
    this.renderReenter();
  }

  private get touchScreen(): boolean {
    return this.platform !== 'other' || (window.matchMedia?.('(hover: none) and (pointer: coarse)').matches ?? false);
  }

  /** Only ever on a tap of the button: one request, nothing retried if the browser says no. */
  private async reenterFullscreen(): Promise<void> {
    if (this.requesting || this.isFullscreen) return;
    this.requesting = true;
    this.renderReenter();
    try {
      await this.toggleFullscreen();
    } finally {
      this.requesting = false;
      this.renderReenter();
    }
  }

  private renderReenter(): void {
    const show = showFullscreenReentry({
      inGame: this.inGame,
      touch: this.touchScreen,
      api: this.api,
      standalone: this.standalone,
      fullscreen: this.isFullscreen,
      pending: this.requesting,
      locationPlateVisible: this.locationPlateVisible,
    });
    // Keep an existing control long enough for the short fade while a plate
    // takes the corner. It is disabled immediately, and is removed for every
    // other reason the control cannot be shown.
    const canShowWithoutPlate = showFullscreenReentry({
      inGame: this.inGame,
      touch: this.touchScreen,
      api: this.api,
      standalone: this.standalone,
      fullscreen: this.isFullscreen,
      pending: this.requesting,
    });
    if (!show && !(this.locationPlateVisible && canShowWithoutPlate && this.reenter)) {
      this.reenter?.remove();
      this.reenter = null;
      return;
    }
    if (!this.reenter) {
      const b = button('pk-shell__reenter', '⛶', () => void this.reenterFullscreen());
      b.setAttribute('aria-label', 'Back to fullscreen');
      b.title = 'Back to fullscreen';
      for (const type of ['pointerdown', 'touchstart', 'mousedown'] as const) {
        b.addEventListener(type, (e) => e.stopPropagation());
      }
      document.body.appendChild(b);
      this.reenter = b;
    }
    const hiddenForPlate = this.locationPlateVisible;
    this.reenter.classList.toggle('pk-shell__reenter--plate-hidden', hiddenForPlate);
    this.reenter.disabled = hiddenForPlate;
    this.reenter.setAttribute('aria-hidden', hiddenForPlate ? 'true' : 'false');
    const canvas = document.querySelector('canvas');
    const rect = canvas?.getBoundingClientRect();
    const scale = rect && rect.width > 0 ? rect.width / 640 : 1;
    this.reenter.style.setProperty('--pk-reenter-left', `${(rect?.left ?? 0) + REENTER_GAME_X * scale}px`);
    this.reenter.style.setProperty('--pk-reenter-top', `${(rect?.top ?? 0) + REENTER_GAME_Y * scale}px`);
  }

  /** Called whenever fullscreen turns on or off (the Options menu redraws). */
  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Must run inside a tap, click or key press: browsers refuse fullscreen
   * otherwise. Resolves to whether the game is fullscreen afterwards.
   */
  async toggleFullscreen(): Promise<boolean> {
    const doc = document as WebkitDocument;
    const root = document.documentElement as WebkitElement;
    try {
      if (this.isFullscreen) {
        if (doc.exitFullscreen) await doc.exitFullscreen();
        else await doc.webkitExitFullscreen?.();
      } else if (this.api === 'standard') {
        await root.requestFullscreen({ navigationUI: 'hide' });
        void (screen.orientation as LockableOrientation | undefined)?.lock?.('landscape').catch(() => undefined);
      } else if (this.api === 'webkit') {
        await root.webkitRequestFullscreen?.();
      }
    } catch {
      // Refused (no gesture, or the browser said no): the game carries on windowed.
    }
    this.viewportChanged();
    return this.isFullscreen;
  }

  /** A new version is waiting; the title screen offers it, nothing reloads on its own. */
  offerUpdate(apply: () => void): void {
    this.update = { apply };
    this.renderUpdate();
  }

  private viewportChanged(): void {
    const refresh = () => this.refreshGame?.();
    requestAnimationFrame(refresh);
    window.setTimeout(refresh, LATE_REFRESH_MS);
    document.documentElement.classList.toggle('pk-standalone', this.standalone);
    this.renderCorner();
    this.renderReenter();
    window.setTimeout(() => this.renderReenter(), LATE_REFRESH_MS);
    for (const l of [...this.listeners]) l();
  }

  private renderCorner(): void {
    this.corner?.remove();
    this.corner = null;
    if (!this.titleActive) return;
    const corner = el('div', 'pk-shell__corner');
    const offer = displayOffer({ api: this.api, platform: this.platform, standalone: this.standalone });
    if (offer === 'fullscreen') {
      corner.appendChild(
        button('pk-shell__fullscreen', this.isFullscreen ? '✕ Exit Fullscreen' : '⛶ Play Fullscreen', () => {
          void this.toggleFullscreen();
        }),
      );
    }
    const hint = readHint();
    if (!this.standalone && hint !== 'never' && hint !== 'installed') {
      if (this.installPrompt) {
        corner.appendChild(button('pk-shell__install', '＋ Install', () => void this.promptInstall()));
      } else if (offer === 'ios-home-screen') {
        corner.appendChild(button('pk-shell__install', '＋ Add to Home Screen', () => this.openIosSheet()));
      }
    }
    if (!corner.childElementCount) return;
    document.body.appendChild(corner);
    this.corner = corner;
  }

  private async promptInstall(): Promise<void> {
    const prompt = this.installPrompt;
    if (!prompt) return;
    this.installPrompt = null;
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      writeHint(choice.outcome === 'accepted' ? 'installed' : 'never');
    } catch {
      // The browser withdrew the offer.
    }
    this.renderCorner();
  }

  private openIosSheet(): void {
    this.closeSheet();
    const sheet = el('div', 'pk-shell__sheet');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-label', 'Add Project Pancake to your Home Screen');
    const card = el('div', 'pk-shell__card');
    const steps = el('ol', 'pk-shell__steps');
    for (const text of [
      'Tap the Share button (the square with the arrow) in Safari.',
      'Choose “Add to Home Screen”, then Add.',
      'Open Pancake from your Home Screen: it plays full screen, like an app.',
    ]) {
      steps.appendChild(el('li', '', text));
    }
    const buttons = el('div', 'pk-shell__row');
    buttons.append(
      button('pk-shell__primary', 'Got it', () => this.closeSheet()),
      button('pk-shell__quiet', "Don't show again", () => {
        writeHint('never');
        this.closeSheet();
        this.renderCorner();
      }),
    );
    card.append(
      el('p', 'pk-shell__title', 'Play full screen on iPhone'),
      steps,
      el('p', 'pk-shell__note', 'The Home Screen app asks for the birthday code once more, then remembers it.'),
      buttons,
    );
    sheet.appendChild(card);
    sheet.addEventListener('click', (e) => {
      if (e.target === sheet) this.closeSheet();
    });
    document.body.appendChild(sheet);
    this.sheet = sheet;
    card.querySelector<HTMLButtonElement>('.pk-shell__primary')?.focus({ preventScroll: true });
  }

  private closeSheet(): void {
    this.sheet?.remove();
    this.sheet = null;
  }

  private renderUpdate(): void {
    this.updateCard?.remove();
    this.updateCard = null;
    if (!this.titleActive || !this.update) return;
    const card = el('div', 'pk-shell__update');
    card.setAttribute('role', 'status');
    const { apply } = this.update;
    card.append(
      el('span', '', 'A fresh version of Pancake is ready.'),
      button('pk-shell__primary', 'Refresh', () => {
        card.remove();
        apply();
      }),
      button('pk-shell__quiet', 'Later', () => {
        this.update = null;
        card.remove();
      }),
    );
    document.body.appendChild(card);
    this.updateCard = card;
  }

  /** Shown by CSS on touch screens held upright; "Play upright anyway" dismisses it for this visit. */
  private mountRotateCard(): void {
    const card = el('div', 'pk-rotate');
    card.setAttribute('role', 'note');
    const icon = el('div', 'pk-rotate__phone');
    icon.setAttribute('aria-hidden', 'true');
    card.append(
      icon,
      el('p', 'pk-rotate__title', 'Turn your phone sideways'),
      el('p', 'pk-rotate__text', 'Pancake’s adventure is made for landscape.'),
      button('pk-shell__quiet pk-rotate__skip', 'Play upright anyway', () => {
        document.documentElement.classList.add('pk-portrait-ok');
      }),
    );
    document.body.appendChild(card);
  }
}

export const displayShell = new DisplayShell();
